import { Switch, Route, Router as WouterRouter, useLocation, Redirect } from "wouter";
import { useEffect, useState, lazy, Suspense, type ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { MotionConfig } from "framer-motion";

import Home from "@/pages/home";
import NotFound from "@/pages/not-found";
import { TooltipProvider } from "@/components/ui/tooltip";

/**
 * Route-level code splitting that hydrates cleanly.
 *
 * Plain React.lazy() suspends on its first render even when the chunk is
 * already cached, so hydrating a prerendered page rendered the Suspense
 * fallback instead of the page and React threw a hydration mismatch (#418),
 * discarded the server HTML, and re-rendered from scratch. lazyPage() adds a
 * preload() that main.tsx awaits before hydrateRoot, and each mounted
 * instance renders the already-loaded module directly, with no Suspense
 * boundary, so the first client render matches the prerendered HTML exactly.
 * The Suspense boundary lives inside lazyPage and only wraps pages that
 * still have to load (client-side navigation).
 */
type PageModule = { default: ComponentType<any> };
type PreloadablePage = ComponentType<any> & { preload: () => Promise<PageModule> };

function lazyPage(factory: () => Promise<PageModule>): PreloadablePage {
  let loaded: ComponentType<any> | null = null;
  const load = () =>
    factory().then((m) => {
      loaded = m.default;
      return m;
    });
  const Lazy = lazy(load);
  const Page = (props: Record<string, unknown>) => {
    // Decided once per mount so a page never swaps component types (and
    // remounts) mid-life.
    const [Comp] = useState<ComponentType<any> | null>(() => loaded);
    if (Comp) return <Comp {...props} />;
    // Only pages that aren't loaded yet get a Suspense boundary. The
    // prerendered HTML is a client render snapshot with no Suspense
    // markers, so a boundary around a hydrating page would mismatch.
    return (
      <Suspense fallback={<RouteFallback />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
  (Page as PreloadablePage).preload = load;
  return Page as PreloadablePage;
}

const About = lazyPage(() => import("@/pages/about"));
const Books = lazyPage(() => import("@/pages/books"));
const BookYMIFOS = lazyPage(() => import("@/pages/book-ymifos"));
const BookWorkbook = lazyPage(() => import("@/pages/book-workbook"));
const BookHavoc = lazyPage(() => import("@/pages/book-havoc"));
const Perspectives = lazyPage(() => import("@/pages/perspectives"));
const PerspectiveDigitalMentalHealth = lazyPage(() => import("@/pages/perspective-digital-mental-health"));
const PerspectiveModernPsychiatry = lazyPage(() => import("@/pages/perspective-modern-psychiatry"));
const PerspectiveAdviceFails = lazyPage(() => import("@/pages/perspective-advice-fails"));
const PerspectiveCareFiveOptions = lazyPage(() => import("@/pages/perspective-care-five-options"));
const PerspectiveHighPerformers = lazyPage(() => import("@/pages/perspective-high-performers"));
const PerspectiveThoughtVsThinking = lazyPage(() => import("@/pages/perspective-thought-vs-thinking"));
const PerspectiveMindLoudestRest = lazyPage(() => import("@/pages/perspective-mind-loudest-rest"));
const PerspectiveThinkingHarderAnxiety = lazyPage(() => import("@/pages/perspective-thinking-harder-anxiety"));
const PerspectiveReplaysConversations = lazyPage(() => import("@/pages/perspective-replays-conversations"));
const PerspectiveNervousSystemUrgent = lazyPage(() => import("@/pages/perspective-nervous-system-urgent"));
const Projects = lazyPage(() => import("@/pages/projects"));
const Media = lazyPage(() => import("@/pages/media"));
const Speaking = lazyPage(() => import("@/pages/speaking"));
const Contact = lazyPage(() => import("@/pages/contact"));
const Disclaimer = lazyPage(() => import("@/pages/disclaimer"));
const Terms = lazyPage(() => import("@/pages/terms"));
const Privacy = lazyPage(() => import("@/pages/privacy"));
const Accessibility = lazyPage(() => import("@/pages/accessibility"));
const CopyrightPage = lazyPage(() => import("@/pages/copyright"));
const CookiesPage = lazyPage(() => import("@/pages/cookies"));
const MediaInquiries = lazyPage(() => import("@/pages/media-inquiries"));
const EditorialProcess = lazyPage(() => import("@/pages/editorial-process"));
const FAQ = lazyPage(() => import("@/pages/faq"));
const Press = lazyPage(() => import("@/pages/press"));
const Notifications = lazyPage(() => import("@/pages/notifications"));
const AIPolicy = lazyPage(() => import("@/pages/ai-policy"));
const DisclosuresPage = lazyPage(() => import("@/pages/disclosures"));
const CorrectionsPage = lazyPage(() => import("@/pages/corrections"));
const EvidencePage = lazyPage(() => import("@/pages/evidence"));
const CommunityGuidelinesPage = lazyPage(() => import("@/pages/community-guidelines"));
const MedicalReviewBoard = lazyPage(() => import("@/pages/medical-review-board"));
const FounderOfTheShrinkNetwork = lazyPage(() => import("@/pages/founder-of-the-shrink-network"));
const WorkWithDrRefai = lazyPage(() => import("@/pages/work-with-dr-refai"));
const MedicalReviewer = lazyPage(() => import("@/pages/medical-reviewer"));
const WhatImBuilding = lazyPage(() => import("@/pages/what-im-building"));
const WhyShrink = lazyPage(() => import("@/pages/why-shrink"));
const Verified = lazyPage(() => import("@/pages/verified"));
const SecurityPolicy = lazyPage(() => import("@/pages/security"));

const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);

const queryClient = new QueryClient();

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);
  return null;
}

function RouteFallback() {
  return <div aria-busy="true" style={{ minHeight: "100vh" }} />;
}

// Open links to OTHER websites (different origin) in a new browser tab, while
// internal page navigation stays in the same tab (handled by wouter as usual).
// A single capture-phase listener intercepts the click before wouter does.
function OpenExternalLinksInNewTab() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleClick = (event: MouseEvent) => {
      // Respect modified clicks and non-primary buttons (browser handles those).
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      )
        return;

      const anchor = (event.target as Element | null)?.closest?.("a");
      if (!anchor) return;

      // Opt-out for links that manage their own window (e.g. share popups).
      if (anchor.hasAttribute("data-newtab-ignore")) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref || anchor.hasAttribute("download")) return;

      // Leave in-page anchors and non-navigational protocols alone.
      if (
        rawHref.startsWith("#") ||
        /^(mailto:|tel:|sms:|javascript:)/i.test(rawHref)
      )
        return;

      // Only act on external links (different origin). Internal page
      // navigation stays in the same tab and is handled by wouter as usual.
      if (anchor.origin === window.location.origin) return;

      // For external links, preventDefault stops the current tab from also
      // navigating, then open the destination in a new tab.
      event.preventDefault();
      window.open(anchor.href, "_blank", "noopener,noreferrer");
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  return null;
}

const PRELOADABLE: Record<string, PreloadablePage> = {
  "/about": About,
  "/books": Books,
  "/books/your-mind-is-full-of-shit": BookYMIFOS,
  "/books/your-mind-is-full-of-shit-workbook": BookWorkbook,
  "/books/the-havoc-in-your-head": BookHavoc,
  "/perspectives": Perspectives,
  "/perspectives/digital-mental-health-needs-more-than-convenience": PerspectiveDigitalMentalHealth,
  "/perspectives/what-modern-psychiatry-gets-right": PerspectiveModernPsychiatry,
  "/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment": PerspectiveAdviceFails,
  "/perspectives/why-we-present-care-as-five-options-not-one": PerspectiveCareFiveOptions,
  "/perspectives/why-high-performers-can-still-feel-anxious": PerspectiveHighPerformers,
  "/perspectives/difference-between-a-thought-and-thinking": PerspectiveThoughtVsThinking,
  "/perspectives/why-your-mind-feels-loudest-when-trying-to-rest": PerspectiveMindLoudestRest,
  "/perspectives/why-thinking-harder-usually-makes-anxiety-worse": PerspectiveThinkingHarderAnxiety,
  "/perspectives/why-your-brain-replays-conversations-long-after-they-end": PerspectiveReplaysConversations,
  "/perspectives/why-your-nervous-system-thinks-everything-is-urgent": PerspectiveNervousSystemUrgent,
  "/projects": Projects,
  "/media": Media,
  "/speaking": Speaking,
  "/contact": Contact,
  "/disclaimer": Disclaimer,
  "/terms": Terms,
  "/privacy": Privacy,
  "/accessibility": Accessibility,
  "/copyright": CopyrightPage,
  "/cookies": CookiesPage,
  "/media-inquiries": MediaInquiries,
  "/editorial-process": EditorialProcess,
  "/faq": FAQ,
  "/press": Press,
  "/notifications": Notifications,
  "/ai-policy": AIPolicy,
  "/disclosures": DisclosuresPage,
  "/corrections": CorrectionsPage,
  "/evidence": EvidencePage,
  "/community-guidelines": CommunityGuidelinesPage,
  "/medical-review-board": MedicalReviewBoard,
  "/founder-of-the-shrink-network": FounderOfTheShrinkNetwork,
  "/work-with-dr-refai": WorkWithDrRefai,
  "/medical-reviewer": MedicalReviewer,
  "/what-im-building": WhatImBuilding,
  "/why-shrink": WhyShrink,
  "/verified": Verified,
  "/security": SecurityPolicy,
};

/** Load the current route's chunk before hydration (see lazyPage). */
export function preloadRoute(pathname: string): Promise<unknown> {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const page = PRELOADABLE[path];
  return page ? page.preload() : Promise.resolve();
}

function Router() {
  return (
    <>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/books" component={Books} />
        <Route path="/books/your-mind-is-full-of-shit" component={BookYMIFOS} />
        <Route path="/books/your-mind-is-full-of-shit-workbook" component={BookWorkbook} />
        <Route path="/books/the-havoc-in-your-head" component={BookHavoc} />
        <Route path="/perspectives" component={Perspectives} />
        <Route
          path="/perspectives/digital-mental-health-needs-more-than-convenience"
          component={PerspectiveDigitalMentalHealth}
        />
        <Route
          path="/perspectives/what-modern-psychiatry-gets-right"
          component={PerspectiveModernPsychiatry}
        />
        <Route
          path="/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment"
          component={PerspectiveAdviceFails}
        />
        <Route
          path="/perspectives/why-we-present-care-as-five-options-not-one"
          component={PerspectiveCareFiveOptions}
        />
        <Route path="/perspectives/why-mental-health-advice-fails-in-the-moment">
          <Redirect to="/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment" />
        </Route>
        <Route
          path="/perspectives/why-high-performers-can-still-feel-anxious"
          component={PerspectiveHighPerformers}
        />
        <Route
          path="/perspectives/difference-between-a-thought-and-thinking"
          component={PerspectiveThoughtVsThinking}
        />
        <Route
          path="/perspectives/why-your-mind-feels-loudest-when-trying-to-rest"
          component={PerspectiveMindLoudestRest}
        />
        <Route
          path="/perspectives/why-thinking-harder-usually-makes-anxiety-worse"
          component={PerspectiveThinkingHarderAnxiety}
        />
        <Route
          path="/perspectives/why-your-brain-replays-conversations-long-after-they-end"
          component={PerspectiveReplaysConversations}
        />
        <Route
          path="/perspectives/why-your-nervous-system-thinks-everything-is-urgent"
          component={PerspectiveNervousSystemUrgent}
        />
        <Route path="/projects" component={Projects} />
        <Route path="/media" component={Media} />
        <Route path="/speaking" component={Speaking} />
        <Route path="/contact" component={Contact} />
        <Route path="/disclaimer" component={Disclaimer} />
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/accessibility" component={Accessibility} />
        <Route path="/copyright" component={CopyrightPage} />
        <Route path="/cookies" component={CookiesPage} />
        <Route path="/media-inquiries" component={MediaInquiries} />
        <Route path="/editorial-process" component={EditorialProcess} />
        <Route path="/faq" component={FAQ} />
        <Route path="/press" component={Press} />
        <Route path="/notifications" component={Notifications} />
        <Route path="/ai-policy" component={AIPolicy} />
        <Route path="/disclosures" component={DisclosuresPage} />
        <Route path="/corrections" component={CorrectionsPage} />
        <Route path="/evidence" component={EvidencePage} />
        <Route path="/community-guidelines" component={CommunityGuidelinesPage} />
        <Route path="/medical-review-board" component={MedicalReviewBoard} />
        <Route path="/founder-of-the-shrink-network" component={FounderOfTheShrinkNetwork} />
        <Route path="/work-with-dr-refai" component={WorkWithDrRefai} />
        <Route path="/medical-reviewer" component={MedicalReviewer} />
        <Route path="/what-im-building" component={WhatImBuilding} />
        <Route path="/why-shrink" component={WhyShrink} />
        <Route path="/verified" component={Verified} />
        <Route path="/security" component={SecurityPolicy} />
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

function DeferredOverlays() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const idle =
      (window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }).requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 1));
    const handle = idle(() => setReady(true), { timeout: 2000 });
    return () => {
      if (typeof handle === "number") window.clearTimeout(handle);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <MotionConfig reducedMotion="user">
          <TooltipProvider>
            <OpenExternalLinksInNewTab />
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <ScrollToTop />
              <Router />
            </WouterRouter>
            <DeferredOverlays />
          </TooltipProvider>
        </MotionConfig>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
